import { useCallback, useEffect, useState } from 'react';
import { App, Button, Form, Input, Modal, Select, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { createDelegation, listDelegations, revokeDelegation, type Delegation } from '@/api/teamAccess';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import { EmptyState } from '@/components/EmptyState';
import { useAuth } from '@/auth/OlAuthContext';
import { formatAbsolute } from '@/lib/time';

const { Paragraph, Text } = Typography;

interface CreateForm {
  delegateType: 'USER' | 'ROLE';
  delegate: string;
  scopeType: 'SPOKE' | 'PROJECT';
  scopeId: string;
  powers: string[];
  reasonCode: string;
}

/**
 * B1 · UC2 §7 — delegated administration (Team & Access). A delegation is a ROW, never a role:
 * scoped powers {USERS, GRANTS, PROJECTS} union into the delegate's effective keys at the RBAC
 * stage; the five anti-widening rules are enforced where the delegate ACTS. delegation.* itself
 * is recovery-plane — only a matrix hub_admin ever sees the write affordances, and the server
 * re-checks regardless (§12).
 */
export function DelegationsPanel() {
  const { message } = App.useApp();
  const { user } = useAuth();
  const canWrite = user?.roles?.includes('hub_admin') ?? false;
  const [delegations, setDelegations] = useState<Delegation[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<CreateForm>();

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    try {
      setDelegations(await listDelegations());
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: ColumnsType<Delegation> = [
    {
      title: 'Delegate',
      key: 'delegate',
      render: (_: unknown, d: Delegation) => (
        <span>
          <Tag style={{ marginRight: 6 }}>{d.delegateType}</Tag>
          <Text code>{d.delegate}</Text>
        </span>
      ),
    },
    {
      title: 'Scope',
      key: 'scope',
      render: (_: unknown, d: Delegation) => (
        <span>
          <Tag style={{ marginRight: 6 }}>{d.scopeType}</Tag>
          <Text code>{d.scopeId}</Text>
        </span>
      ),
    },
    {
      title: 'Powers',
      dataIndex: 'powers',
      render: (powers: string[]) => (
        <span style={{ display: 'inline-flex', gap: 4 }}>
          {powers.map((p) => (
            <Tag key={p} color="blue" style={{ margin: 0 }}>
              {p}
            </Tag>
          ))}
        </span>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_: unknown, d: Delegation) =>
        d.revokedAt ? <Tag>revoked</Tag> : <Tag color="green">live</Tag>,
    },
    { title: 'Granted', dataIndex: 'grantedAt', width: 170, render: (v) => formatAbsolute(v) },
    ...(canWrite
      ? [
          {
            title: '',
            key: 'actions',
            width: 90,
            render: (_: unknown, d: Delegation) =>
              !d.revokedAt ? (
                <Button size="small" danger onClick={async () => {
                  try {
                    await revokeDelegation(d.delegationId);
                    message.success('Delegation revoked — bites on the next request');
                    void load();
                  } catch (e) {
                    message.error(normalizeApiError(e).message);
                  }
                }}>
                  Revoke
                </Button>
              ) : null,
          },
        ]
      : []),
  ];

  if (unavailable) {
    return <EmptyState title="Delegations are not available" description="The IAM UC2 surface is not enabled in this deployment." />;
  }

  return (
    <div>
      <Paragraph type="secondary" style={{ marginBottom: 12, maxWidth: 780, fontSize: 13 }}>
        Scoped admin without new admins: USERS (invite/roles/enable inside the scope), GRANTS (case
        access inside the scope), PROJECTS (spoke-pinned projects). A delegate can never touch a hub
        admin, exceed their own authority, widen delegations, or brick the last hub admin — refused
        server-side, rule by rule.
      </Paragraph>
      {canWrite && (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)} style={{ marginBottom: 12 }}>
          New delegation
        </Button>
      )}
      <Table rowKey="delegationId" size="small" columns={columns} dataSource={delegations}
             loading={loading} pagination={{ pageSize: 20, hideOnSinglePage: true }} />

      <Modal title="New delegation" open={modalOpen} onCancel={() => setModalOpen(false)}
             onOk={() => form.submit()} confirmLoading={submitting} okText="Delegate">
        <Form form={form} layout="vertical"
              initialValues={{ delegateType: 'USER', scopeType: 'SPOKE', reasonCode: 'TEAM_LEAD' }}
              onFinish={async (v) => {
                setSubmitting(true);
                try {
                  await createDelegation({
                    delegateType: v.delegateType, delegate: v.delegate.trim(),
                    scopeType: v.scopeType, scopeId: v.scopeId.trim(),
                    powers: v.powers, reasonCode: v.reasonCode,
                  });
                  message.success('Delegation created');
                  setModalOpen(false);
                  form.resetFields();
                  void load();
                } catch (e) {
                  message.error(normalizeApiError(e).message);
                } finally {
                  setSubmitting(false);
                }
              }}>
          <Form.Item name="delegateType" label="Delegate type" rules={[{ required: true }]}>
            <Select options={[{ value: 'USER' }, { value: 'ROLE' }]} />
          </Form.Item>
          <Form.Item name="delegate" label="Delegate (user sub or role name)" rules={[{ required: true }]}>
            <Input placeholder="a4f2… | analyst | x_team_lead" />
          </Form.Item>
          <Form.Item name="scopeType" label="Scope type" rules={[{ required: true }]}>
            <Select options={[{ value: 'SPOKE' }, { value: 'PROJECT' }]} />
          </Form.Item>
          <Form.Item name="scopeId" label="Scope id (spoke id or project id)" rules={[{ required: true }]}>
            <Input placeholder="s1 | 01KY…" />
          </Form.Item>
          <Form.Item name="powers" label="Powers" rules={[{ required: true, message: 'at least one power' }]}>
            <Select mode="multiple"
                    options={[{ value: 'USERS' }, { value: 'GRANTS' }, { value: 'PROJECTS' }]} />
          </Form.Item>
          <Form.Item name="reasonCode" label="Reason" rules={[{ required: true }]}>
            <Select options={[
              { value: 'TEAM_LEAD' }, { value: 'COVERAGE' }, { value: 'MIGRATION' },
              { value: 'ESCALATION' }, { value: 'OTHER' },
            ]} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
